import { RouteComponentProps } from "@reach/router";
import {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";

import Button from "../components/button/Button";
import Table, { Column } from "../components/table/Table";
import LayoutContext from "../context/layout-context";
import { initialPaginationData } from "../utils/constants";
import {
  PortMappingCreate,
  PortMappingCreations
} from "../utils/helpers/types";
import ContextMenu, {
  ContextOption
} from "../components/context-menu/ContextMenu";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import {
  activatePortMapping,
  createPortMapping,
  deactivatePortMapping,
  deletePortMapping,
  getPortMapping,
  getPortMappings,
  updatePortMapping
} from "../utils/helpers/data/port-mapping";
import styles from "./self-service.module.scss";
import Modal, { ModalProps } from "../components/modal/Modal";
import { resources } from "../utils/resources";
import Input from "../components/input/Input";
import style from "./history.module.scss";

const PortMapping: FunctionComponent<RouteComponentProps> = () => {
  const [loading, setLoading] = useState(false);
  const [reset, setReset] = useState(false);
  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [portMappingOnView, setPortMappingOnView] =
    useState<PortMappingCreations | null>(null);
  const { notify, confirm } = useContext(LayoutContext);
  const [portMapping, setPortMapping] = useState<PortMappingCreations[]>([]);
  const [portMappingOnEdit, setPortMappingOnEdit] =
    useState<PortMappingCreations | null>(null);
  const [showAddEditModal, setShowAddEditModal] = useState(false);

  const [issuerCode, setIssuerCode] = useState<string>("");

  const showResetFilter = issuerCode;

  const resetFilters = () => {
    setIssuerCode("");

    setPagination({ ...pagination, pageNumber: 1 });
    setReset(true);
  };

  const displayAddEditModal = (
    PortMappingCreation: PortMappingCreations | null
  ) => {
    setPortMappingOnEdit(PortMappingCreation);
    setShowAddEditModal(true);
  };

  const dismissAddEditModal = () => {
    setPortMappingOnEdit(null);
    setShowAddEditModal(false);
  };

  const fetchPortMapping = async (
    pageNumber = pagination.pageNumber,
    pageSize = pagination.pageSize
  ) => {
    setLoading(true);
    const { data, error, message } = await getPortMappings(
      pageNumber,
      pageSize,
      issuerCode
    );
    setLoading(false);

    if (error || !data) {
      notify("error", `Unable to fetch port mappings: ${message}`);
      return;
    }

    setPortMapping(data.records);
    setPagination({
      ...pagination,
      pageNumber,
      total: data.total
    });

    setReset(false);
  };

  const handleViewReport = async () => {
    setLoading(true);
    try {
      setPagination({ ...pagination, pageNumber: 1 });
      await fetchPortMapping(pagination.pageNumber);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      notify("error", "An error occurred while fetching map acquirer.", error);
    }
  };

  useEffect(() => {
    fetchPortMapping();
  }, [pagination.pageSize]);

  useEffect(() => {
    if (!reset) {
      fetchPortMapping();
    } else {
      // Reset logic
      setPagination({ ...initialPaginationData });
      setReset(false);
    }
  }, [reset]);

  const handleOperation = async (operation: any, id: any) => {
    setLoading(true);
    let result;

    switch (operation) {
      case "delete":
        result = await deletePortMapping(id);
        break;
      case "activate":
        result = await activatePortMapping(id);
        break;
      case "deactivate":
        result = await deactivatePortMapping(id);
        break;
      case "view":
        result = await getPortMapping(id);
        if (!result.error) {
          setPortMappingOnView(result.data);
        }
        break;
      default:
        setLoading(false);
        return;
    }

    setLoading(false);

    if (result.error) {
      notify("error", `Unable to ${operation} port mapping: ${result.message}`);
      return;
    }

    notify("success", `Port mapping ${operation}d successfully`);
  };

  const handleDelete = async (portMapping: PortMappingCreations) => {
    confirm({
      title: "Are you sure you want to delete this port mapping?",
      body: "This action cannot be undone.",
      onOk: async () => {
        setLoading(true);
        const { error, message } = await deletePortMapping(portMapping.id);
        setLoading(false);

        if (error) {
          notify("error", `Unable to delete port mapping: ${message}`);
          return;
        }

        notify("success", "Port mapping deleted successfully");
        await fetchPortMapping();
      }
    });
  };

  const columns: Column<PortMappingCreations>[] = [
    {
      title: "Issuer Code",
      dataIndex: "issuerCode",
      key: "issuerCode"
    },
    {
      title: "IP Address",
      dataIndex: "ipAddress",
      key: "ipAddress"
    },
    {
      title: "Port",
      dataIndex: "port",
      key: "port"
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      render: (active: boolean) => (active ? "true" : "false")
    },

    {
      title: "",
      render: (_: unknown, portMapping: PortMappingCreations) => {
        const options: ContextOption[] = [
          {
            label: "View Details",
            onClick: () => {
              handleOperation("view", portMapping.id);
            }
          },
          {
            label: "Edit",
            onClick: () => {
              displayAddEditModal(portMapping);
            }
          },
          {
            label: "Delete",
            onClick: () => handleDelete(portMapping)
          },
          {
            label: "Activate",
            onClick: () => handleOperation("activate", portMapping.id)
          },
          {
            label: "Deactivate",
            onClick: () => handleOperation("deactivate", portMapping.id)
          }
        ].filter(BooleanFilter) as ContextOption[];
        return <ContextMenu options={options} />;
      }
    }
  ];
  return (
    <main>
      <h1 className="title vertical-margin spaced thin">Port mapping</h1>

      <div className="vertical-margin spaced">
        <div className={style.cont}>
          <div className={style.container}>
            <div className={style.cont1}>
              <div className="input-group half-width">
                <Input
                  value={issuerCode}
                  onChange={(value: string) => setIssuerCode(value)}
                  placeholder="Enter issuer code "
                />
              </div>
            </div>

            <div className={style.inputView}>
              <Button
                type="accent"
                size="large"
                className="strong no-pdf-print"
                onClick={handleViewReport}
              >
                Filter record
              </Button>

              <span className={style.button} />
            </div>
          </div>
        </div>
        {showResetFilter && (
          <Button type="accent" size="small" onClick={resetFilters}>
            RESET FILTERS
          </Button>
        )}
      </div>

      <br />
      <br />

      <Table
        dataSource={portMapping || []}
        columns={columns}
        pagination={pagination}
        onPageChange={fetchPortMapping}
        onPageSizeChange={pageSize =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
        loading={loading}
        className="smaller"
      />

      <PortMappingViewModal
        portMapping={portMappingOnView}
        cancel={() => setPortMappingOnView(null)}
        visible={Boolean(portMappingOnView)}
      />

      <AddEditModal
        visible={showAddEditModal}
        portMappingOnEdit={portMappingOnEdit}
        cancel={dismissAddEditModal}
        refresh={(stayOnPage: any) =>
          fetchPortMapping(stayOnPage ? pagination.pageNumber : 1)
        }
      />
    </main>
  );
};

const blankappService: PortMappingCreate = {
  issuerCode: "",
  ipAddress: "",
  port: 0
};

const AddEditModal: FunctionComponent<
  ModalProps & {
    portMappingOnEdit: PortMappingCreations | null;
    refresh: (stayOnPage: boolean) => void;
    cancel: () => void;
  }
> = ({ visible, cancel, portMappingOnEdit, refresh }) => {
  const [formData, setFormData] = useState<PortMappingCreate>({
    ...blankappService,
    ...portMappingOnEdit
  });

  const [isSaving, setIsSaving] = useState(false);
  const { notify } = useContext(LayoutContext);

  const handleChange = (key: keyof PortMappingCreate, value: unknown) => {
    setFormData(prevFormData => ({ ...prevFormData, [key]: value }));
  };

  useEffect(() => {
    setFormData(
      portMappingOnEdit
        ? { ...blankappService, ...portMappingOnEdit }
        : { ...blankappService }
    );
  }, [visible, portMappingOnEdit]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setIsSaving(true);

    if (formData.ipAddress.length > 16) {
      notify("error", "Ip address must not exceed 16 characters");
      setIsSaving(false);
      return;
    }

    const portRegex = /^\d{1,10}$/;
    if (!portRegex.test(String(formData.port))) {
      notify(
        "error",
        "Port must be numeric and should not exceed 10 characters"
      );
      setIsSaving(false);
      return;
    }

    let id: number | undefined;

    if (portMappingOnEdit && typeof portMappingOnEdit.id === "number") {
      id = portMappingOnEdit.id;
    }

    if (id !== undefined) {
      const { error, message } = await updatePortMapping(id, {
        ...formData
      });

      if (error) {
        notify("error", `Unable to update port mapping: ${message}`);
        setIsSaving(false);
        return;
      }

      notify("success", "Successfully updated port mapping");
    }
    cancel?.();
    refresh(false);
    setIsSaving(false);
  };

  useEffect(() => {
    if (!isCreateOperation && portMappingOnEdit) {
      setFormData({
        port: portMappingOnEdit.port,
        issuerCode: portMappingOnEdit.issuerCode,
        ipAddress: portMappingOnEdit.ipAddress
      });
    }
  }, [visible, portMappingOnEdit]);

  const isCreateOperation = portMappingOnEdit === null;

  return (
    <Modal cancel={cancel} visible={visible} size="small">
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Issuer Code</span>
          </div>
          <Input
            value={formData.issuerCode}
            onChange={value => handleChange("issuerCode", value)}
            placeholder="Enter issuerCode"
            responsive
            required
            disabled={!isCreateOperation}
          />
        </div>

        <div className="input-group">
          <span className="question">Port</span>
          <Input
            value={formData.port}
            onChange={value => handleChange("port", value)}
            placeholder="Enter port"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Ip Address</span>
          </div>
          <Input
            value={formData.ipAddress}
            onChange={value => handleChange("ipAddress", value)}
            placeholder="Enter Ip Address"
            responsive
            required
          />
        </div>

        <br />
        <div className="flex spaced end vertical-margin">
          <Button type="accent" onClick={() => cancel?.()}>
            Cancel
          </Button>
          <Button buttonType="submit" loading={isSaving}>
            Edit Port Mapping
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface portMappingEditModalProps {
  portMapping: PortMappingCreations | null;
  cancel: () => void;
  visible: boolean;
}

const PortMappingViewModal: FunctionComponent<portMappingEditModalProps> = ({
  portMapping: _portMapping,
  cancel,
  visible
}) => {
  const [loading] = useState(false);
  const [portMapping, setPortMapping] = useState<PortMappingCreations | null>(
    _portMapping
  );

  useEffect(() => {
    setPortMapping(_portMapping);
  }, [_portMapping]);

  const formatDate = (date: string): string => {
    const formattedDate = new Date(date)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      })
      .replace(/\//g, "-");
    return formattedDate;
  };

  return (
    <Modal visible={visible} cancel={cancel} ignoreBackdropClicks>
      {loading ? (
        <img
          alt="loading"
          src={resources.images.LOADING_TYPING}
          className="generic-icon xxl"
        />
      ) : (
        <section className={styles.details}>
          <div className={styles.detail}>
            <span className={styles.key}>Id:</span>
            <span className={styles.value}>{portMapping?.id || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Port:</span>
            <span className={styles.value}>{portMapping?.port || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Issuer Code:</span>
            <span className={styles.value}>
              {portMapping?.issuerCode || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Ip Address:</span>
            <span className={styles.value}>
              {portMapping?.ipAddress || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Active:</span>
            <span className={styles.value}>
              {portMapping?.active !== undefined
                ? portMapping?.active.toString()
                : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Created by:</span>
            <span className={styles.value}>
              {portMapping?.createdBy || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Created on:</span>
            <span className={styles.value}>
              {portMapping?.createdOn
                ? formatDate(portMapping.createdOn)
                : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Last updated by:</span>
            <span className={styles.value}>
              {portMapping?.lastUpdatedBy || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Last updated on:</span>
            <span className={styles.value}>
              {portMapping?.lastUpdatedOn
                ? formatDate(portMapping.lastUpdatedOn)
                : "N/A"}
            </span>
          </div>
        </section>
      )}
    </Modal>
  );
};

export default PortMapping;
